import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Cookies from 'js-cookie';

import en from './locales/en.json';
import de from './locales/de.json';
import fr from './locales/fr.json';

import { setGoogleTranslateLanguage, initGoogleTranslate } from './googleTranslate';

/**
 * Language strategy:
 *   • HARDCODED_LANGUAGES  → react-i18next JSON files (premium hand-crafted)
 *   • REMAINING languages  → dropdown still available, Google Translate does the work
 * The user always sees all 8 in the switcher; under the hood, only 3 have JSON.
 */
export const HARDCODED_LANGUAGES = ['en', 'de', 'fr'];

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',    flag: '🇬🇧', hardcoded: true  },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪', hardcoded: true  },
  { code: 'fr', label: 'Français',   flag: '🇫🇷', hardcoded: true  },
  { code: 'es', label: 'Español',    flag: '🇪🇸', hardcoded: false },
  { code: 'it', label: 'Italiano',   flag: '🇮🇹', hardcoded: false },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱', hardcoded: false },
  { code: 'pl', label: 'Polski',     flag: '🇵🇱', hardcoded: false },
  { code: 'pt', label: 'Português',  flag: '🇵🇹', hardcoded: false },
];

export const LANG_COOKIE = 'jobasti_lang';
export const LANG_LOCAL_STORAGE = 'jobasti_lang';
export const LANG_SUGGESTION_DISMISSED = 'jobasti_lang_suggest_dismissed';

/**
 * Persist a chosen language across every layer:
 *   - localStorage / cookie  (survives across sessions & devices)
 *   - react-i18next runtime  (ONLY if it's a hardcoded language; premium JSON swap)
 *   - Google Translate       (always; translates the rest of the DOM)
 */
export const persistLanguage = (code) => {
  if (!code) return;
  const normalized = String(code).toLowerCase().split('-')[0];
  try { localStorage.setItem(LANG_LOCAL_STORAGE, normalized); } catch { /* noop */ }
  try { Cookies.set(LANG_COOKIE, normalized, { expires: 365, sameSite: 'lax' }); } catch { /* noop */ }

  if (HARDCODED_LANGUAGES.includes(normalized)) {
    // Premium JSON path — react-i18next swaps immediately; Google Translate then
    // covers any un-wrapped strings on internal pages we haven't manually done.
    if (i18n.language !== normalized) i18n.changeLanguage(normalized);
  } else {
    // Not hardcoded — keep react-i18next at English (source) and let Google
    // Translate do the whole page. This avoids showing half-translated UIs.
    if (i18n.language !== 'en') i18n.changeLanguage('en');
  }
  setGoogleTranslateLanguage(normalized);
};

/** Best available "already-picked" language (respects prior user choice). */
export const readStoredLanguage = () => {
  try {
    const ls = localStorage.getItem(LANG_LOCAL_STORAGE);
    if (ls) return ls;
  } catch { /* noop */ }
  try {
    const ck = Cookies.get(LANG_COOKIE);
    if (ck) return ck;
  } catch { /* noop */ }
  return null;
};

/**
 * Return what the browser is asking for, normalised to a supported 2-letter code.
 * Falls back to null if nothing supported matches.
 */
export const detectBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return null;
  const raw = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || navigator.userLanguage || ''];
  for (const l of raw) {
    const code = String(l || '').toLowerCase().split('-')[0];
    if (SUPPORTED_LANGUAGES.some(s => s.code === code)) return code;
  }
  return null;
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      de: { translation: de },
      fr: { translation: fr },
    },
    fallbackLng: 'en',
    // Only 3 in react-i18next; the other 5 are Google-Translate-only
    supportedLngs: HARDCODED_LANGUAGES,
    nonExplicitSupportedLngs: false,
    detection: {
      order: ['localStorage', 'cookie', 'navigator', 'htmlTag'],
      lookupLocalStorage: LANG_LOCAL_STORAGE,
      lookupCookie: LANG_COOKIE,
      // IMPORTANT: don't let the detector write back — it would overwrite the
      // user's actual choice with the fallback ('en') for non-hardcoded langs.
      // Our persistLanguage() is the sole writer to localStorage/cookie.
      caches: [],
    },
    interpolation: { escapeValue: false },
    returnEmptyString: false,
  });

/**
 * On first boot, if the visitor arrived with a non-hardcoded preference
 * stored (e.g. they had picked "es" before we changed strategy), replay
 * persistLanguage so Google Translate hydrates the DOM correctly.
 */
try {
  const stored = readStoredLanguage();
  const active = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  if (stored && stored !== active && SUPPORTED_LANGUAGES.some(l => l.code === stored)) {
    // Fires Google Translate too via persistLanguage
    persistLanguage(stored);
  }
  if (!Cookies.get(LANG_COOKIE)) {
    Cookies.set(LANG_COOKIE, active, { expires: 365, sameSite: 'lax' });
  }
  initGoogleTranslate();
} catch { /* noop */ }

export default i18n;
